import React, { useState, useRef, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";
import { ReactComponent as ChevronDownIcon } from "../../../images/editor-icons/chevron-down.svg";
import { ReactComponent as CheckIcon } from "../../../images/editor-icons/check.svg";
import { TriggerText, ChevronIcon, DropdownContainer, DropdownHeader, SelectAllOption, Checkbox, IndeterminateIcon, OptionLabel, OptionsList, Trigger, Container, Option } from "./Styles/MultiSelectStyles";

const messages = defineMessages({
  placeholder: {
    id: "editor.multiselect.placeholder",
    defaultMessage: "Select options...",
    description: "Placeholder text for multiselect dropdown"
  },
  selectedCount: {
    id: "editor.multiselect.selected-count",
    defaultMessage: "{count, plural, one {# layer} other {# layers} } selected",
    description: "Text showing number of selected layers"
  },
  selectAll: {
    id: "editor.multiselect.select-all",
    defaultMessage: "Select all",
    description: "Button text to select all options"
  },
  deselectAll: {
    id: "editor.multiselect.deselect-all", 
    defaultMessage: "Deselect all",
    description: "Button text to deselect all options"
  }
});

interface MultiSelectOption {
  value: number;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selectedValues: number[];
  onChange: (selectedValues: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder,
  disabled = false,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (value: number) => {
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    
    onChange(newSelectedValues);
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return placeholder || intl.formatMessage(messages.placeholder);
    }
    if (selectedValues.length === 1) {
      const option = options.find(opt => opt.value === selectedValues[0]);
      return option ? option.label : "";
    }
    return intl.formatMessage(messages.selectedCount, { count: selectedValues.length });
  };

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      // Deselect all
      onChange([]);
    } else {
      // Select all
      onChange(options.map(opt => opt.value));
    }
  };

  const allSelected = selectedValues.length === options.length;
  const someSelected = selectedValues.length > 0 && selectedValues.length < options.length;

  return (
    <Container ref={containerRef}>
      <Trigger onClick={handleToggle} disabled={disabled}>
        <TriggerText disabled={disabled}>{getDisplayText()}</TriggerText>
        <ChevronIcon $isOpen={isOpen}>
          <ChevronDownIcon />
        </ChevronIcon>
      </Trigger>

      {isOpen && (
        <DropdownContainer>
          <DropdownHeader>
            <SelectAllOption onClick={handleSelectAll}>
              <Checkbox $checked={allSelected} $indeterminate={someSelected}>
                {allSelected && <CheckIcon />}
                {someSelected && <IndeterminateIcon>-</IndeterminateIcon>}
              </Checkbox>
              <OptionLabel>
                {allSelected 
                  ? intl.formatMessage(messages.deselectAll)
                  : intl.formatMessage(messages.selectAll)
                }
              </OptionLabel>
            </SelectAllOption>
          </DropdownHeader>
          
          <OptionsList>
            {options.map((option) => (
              <Option
                key={option.value}
                onClick={() => handleOptionClick(option.value)}
                $selected={selectedValues.includes(option.value)}
              >
                <Checkbox $checked={selectedValues.includes(option.value)}>
                  {selectedValues.includes(option.value) && <CheckIcon />}
                </Checkbox>
                <OptionLabel>{option.label}</OptionLabel>
              </Option>
            ))}
          </OptionsList>
        </DropdownContainer>
      )}
    </Container>
  );
};

export default MultiSelect;
